import React, { useState, useEffect } from 'react';
import { getAllDealerAssistant, getVfProjectsData } from '../services/api';
import './AgentEditor.css';

const DealerList = () => {
  const [assistants, setAssistants] = useState([]);
  const [filteredAssistants, setFilteredAssistants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'ascending' });
  const [vfProjectsMap, setVfProjectsMap] = useState(new Map());
  
  // Dynamic filtering
  const [filterField, setFilterField] = useState('');
  const [filterValue, setFilterValue] = useState('');
  
  // Column visibility
  const [showColumnModal, setShowColumnModal] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(new Set([
    'name', 'assistant_id', 'model', 'instructions_id', 'tools', 
    'emails', 'adf_emails', 'vf_id'
  ]));
  
  // JSON modal state
  const [jsonModal, setJsonModal] = useState({ open: false, title: '', data: null });

  // Get all possible fields from assistants
  const getAllFields = (assistants) => {
    const fields = new Set();
    assistants.forEach(assistant => {
      Object.keys(assistant).forEach(key => {
        if (key !== 'optionalFields') {
          fields.add(key);
        }
      });
      if (assistant.optionalFields) {
        Object.keys(assistant.optionalFields).forEach(key => {
          fields.add(key);
        });
      }
    });
    return Array.from(fields).sort();
  };

  useEffect(() => {
    const fetchAssistants = async () => {
      try {
        const response = await getAllDealerAssistant();
        setAssistants(response.data.data);
        setFilteredAssistants(response.data.data);
        setLoading(false);
      } catch (err) {
        setError('Error fetching dealer assistants');
        setLoading(false);
      }
    };

    const fetchVfProjects = async () => {
      try {
        const response = await getVfProjectsData();
        const projectsData = response.data || {};
        const map = new Map();
        Object.entries(projectsData).forEach(([key, value]) => {
          map.set(value, key);
        });
        setVfProjectsMap(map);
      } catch (err) {
        console.error('Error fetching VF projects:', err);
      }
    };

    fetchAssistants();
    fetchVfProjects();
  }, []);

  // Update filter field when assistants are loaded
  useEffect(() => {
    if (assistants.length > 0) {
      const fields = getAllFields(assistants);
      // Update filter field options only if not already set
      if (fields.length > 0 && !filterField) {
        setFilterField(fields[0]);
      }
      // Ensure 'name' is always in visibleColumns
      setVisibleColumns(prev => {
        const newSet = new Set(prev);
        newSet.add('name');
        return newSet;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assistants]);

  useEffect(() => {
    let results = assistants;

    // Dynamic filtering
    if (filterField && filterValue) {
      results = results.filter(assistant => {
        let fieldValue;
        
        // Check in optionalFields first, then top level
        if (assistant.optionalFields && assistant.optionalFields[filterField] !== undefined) {
          fieldValue = assistant.optionalFields[filterField];
        } else {
          fieldValue = assistant[filterField];
        }

        if (fieldValue === undefined || fieldValue === null) {
          return false;
        }

        const searchTerm = filterValue.toLowerCase();
        
        // Handle arrays
        if (Array.isArray(fieldValue)) {
          return fieldValue.some(item => 
            String(item).toLowerCase().includes(searchTerm)
          );
        }
        
        // Handle objects (like dealer_doc, webchat_settings, etc.)
        if (typeof fieldValue === 'object') {
          const objStr = JSON.stringify(fieldValue).toLowerCase();
          return objStr.includes(searchTerm);
        }
        
        // Handle strings and numbers
        return String(fieldValue).toLowerCase().includes(searchTerm);
      });
    }

    // Apply sorting
    results.sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];
      
      // Check optionalFields if not found at top level
      if (aValue === undefined && a.optionalFields) {
        aValue = a.optionalFields[sortConfig.key];
      }
      if (bValue === undefined && b.optionalFields) {
        bValue = b.optionalFields[sortConfig.key];
      }
      
      // Handle arrays - compare first element or join
      if (Array.isArray(aValue)) aValue = aValue.join(', ');
      if (Array.isArray(bValue)) bValue = bValue.join(', ');
      
      const aStr = String(aValue || '').toLowerCase();
      const bStr = String(bValue || '').toLowerCase();
      
      if (aStr < bStr) {
        return sortConfig.direction === 'ascending' ? -1 : 1;
      }
      if (aStr > bStr) {
        return sortConfig.direction === 'ascending' ? 1 : -1;
      }
      return 0;
    });

    setFilteredAssistants(results);
  }, [assistants, sortConfig, filterField, filterValue]);

  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'ascending' ? 'descending' : 'ascending'
    }));
  };

  const getSortIndicator = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === 'ascending' ? ' ↑' : ' ↓';
    }
    return '';
  };

  const getFieldValue = (assistant, field) => {
    // Check optionalFields first, then top level
    if (assistant.optionalFields && assistant.optionalFields[field] !== undefined) {
      return assistant.optionalFields[field];
    }
    return assistant[field];
  };

  const formatFieldValue = (value, field) => {
    if (value === undefined || value === null || value === '') {
      return '-';
    }
    
    // Special handling for vf_id
    if (field === 'vf_id') {
      return vfProjectsMap.get(value) || value || '-';
    }
    
    // Handle arrays
    if (Array.isArray(value)) {
      return value.length > 0 ? value.join(', ') : '-';
    }
    
    // Handle objects - return null to indicate it should show a button
    if (typeof value === 'object') {
      return null; // Will be handled in render
    }
    
    // Handle booleans
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    
    return String(value);
  };

  const isObjectField = (value) => {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
  };

  const getFieldLabel = (field) => {
    const labels = {
      name: 'Name',
      assistant_id: 'Assistant ID',
      model: 'AI Model',
      instructions_id: 'Instructions',
      tools: 'Tools',
      emails: 'Emails',
      adf_emails: 'ADF Emails',
      vf_id: 'VF Project',
      dealer_url: 'Dealer URL',
      cms_type: 'CMS Type',
      status: 'Status',
      product: 'Product',
      inventory_id: 'Inventory ID',
      brand_doc_id: 'Brand Doc ID',
      group: 'Group',
      d2c_id: 'D2C ID',
      twilio_numbers: 'Twilio Numbers',
      messenger_ids: 'Messenger IDs',
      service_emails: 'Service Emails',
      confirmation_sms: 'Confirmation SMS',
      activix_hook_sources: 'Activix Hook Sources',
      auto_open_time: 'Auto Open Time',
      auto_open_pages: 'Auto Open Pages',
      embed_element: 'Embed Element',
      color_theme: 'Color Theme',
      image_src: 'Image Source',
      image_settings: 'Image Settings',
      icons: 'Icons',
      dealer_doc: 'Dealer Doc',
      examples: 'Examples',
      timestamps: 'Timestamps',
      webchat_settings: 'Webchat Settings'
    };
    return labels[field] || field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const toggleColumn = (field) => {
    // Prevent deselecting 'name' field
    if (field === 'name') return;
    
    setVisibleColumns(prev => {
      const newSet = new Set(prev);
      if (newSet.has(field)) {
        newSet.delete(field);
      } else {
        newSet.add(field);
      }
      // Always ensure 'name' is included
      newSet.add('name');
      return newSet;
    });
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>{error}</div>;

  const availableFields = getAllFields(assistants);

  return (
    <div>
      <div className="legacy-banner">
        Legacy list view. Prefer the <strong>Agents</strong> editor for nested product packs.
      </div>
      <h2>Dealer List (Legacy)</h2>
      <div style={{ marginBottom: '20px', display: 'flex', gap: '10px', alignItems: 'center' }}>
        <select
          value={filterField}
          onChange={(e) => {
            setFilterField(e.target.value);
            setFilterValue('');
          }}
          style={{ padding: '5px' }}
        >
          <option value="">Select field to filter</option>
          {availableFields.map(field => (
            <option key={field} value={field}>
              {getFieldLabel(field)}
            </option>
          ))}
        </select>
        <input
          type="text"
          placeholder={filterField ? `Search in ${getFieldLabel(filterField)}...` : 'Select a field first'}
          value={filterValue}
          onChange={(e) => setFilterValue(e.target.value)}
          disabled={!filterField}
          style={{ padding: '5px', flex: 1, maxWidth: '300px' }}
        />
        <button
          onClick={() => setShowColumnModal(true)}
          style={{ padding: '5px 15px' }}
        >
          Select Columns
        </button>
        {filterValue && (
          <button
            onClick={() => {
              setFilterField('');
              setFilterValue('');
            }}
            style={{ padding: '5px 15px' }}
          >
            Clear Filter
          </button>
        )}
      </div>

      {/* Column Selection Modal */}
      {showColumnModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            padding: '20px',
            borderRadius: '8px',
            maxWidth: '800px',
            width: '90vw',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <h3>Select Columns to Display</h3>
            <div style={{ marginTop: '15px' }}>
              {availableFields.filter(field => field !== 'name').map(field => (
                <label key={field} style={{ display: 'flex', alignItems: 'center', marginBottom: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={visibleColumns.has(field)}
                    onChange={() => toggleColumn(field)}
                    style={{ marginRight: '6px', flexShrink: 0, width: '16px', height: '16px' }}
                  />
                  {getFieldLabel(field)}
                </label>
              ))}
            </div>
            <div style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
              <button
                onClick={() => {
                  const allFields = new Set(availableFields);
                  allFields.add('name'); // Ensure name is always included
                  setVisibleColumns(allFields);
                }}
                style={{ padding: '5px 15px' }}
              >
                Select All
              </button>
              <button
                onClick={() => {
                  setVisibleColumns(new Set(['name'])); // Always keep name
                }}
                style={{ padding: '5px 15px' }}
              >
                Deselect All
              </button>
              <button
                onClick={() => setShowColumnModal(false)}
                style={{ padding: '5px 15px', marginLeft: 'auto' }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {/* Always render 'name' first */}
              {availableFields.includes('name') && visibleColumns.has('name') && (
                <th
                  onClick={() => handleSort('name')}
                  style={{
                    cursor: 'pointer',
                    padding: '10px',
                    border: '1px solid #ddd',
                    backgroundColor: '#f5f5f5',
                    textAlign: 'left'
                  }}
                >
                  {getFieldLabel('name')}{getSortIndicator('name')}
                </th>
              )}
              {/* Render other columns */}
              {availableFields
                .filter(field => field !== 'name' && visibleColumns.has(field))
                .map(field => (
                  <th
                    key={field}
                    onClick={() => handleSort(field)}
                    style={{
                      cursor: 'pointer',
                      padding: '10px',
                      border: '1px solid #ddd',
                      backgroundColor: '#f5f5f5',
                      textAlign: 'left'
                    }}
                  >
                    {getFieldLabel(field)}{getSortIndicator(field)}
                  </th>
                ))}
            </tr>
          </thead>
          <tbody>
            {filteredAssistants.map((assistant) => (
              <tr key={assistant.assistant_id}>
                {/* Always render 'name' first */}
                {availableFields.includes('name') && visibleColumns.has('name') && (
                  <td
                    style={{
                      padding: '10px',
                      border: '1px solid #ddd',
                      maxWidth: '300px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                    title={formatFieldValue(getFieldValue(assistant, 'name'), 'name')}
                  >
                    {formatFieldValue(getFieldValue(assistant, 'name'), 'name')}
                  </td>
                )}
                {/* Render other columns */}
                {availableFields
                  .filter(field => field !== 'name' && visibleColumns.has(field))
                  .map(field => {
                    const fieldValue = getFieldValue(assistant, field);
                    const formattedValue = formatFieldValue(fieldValue, field);
                    const isObject = isObjectField(fieldValue);
                    
                    return (
                      <td
                        key={field}
                        style={{
                          padding: '10px',
                          border: '1px solid #ddd',
                          maxWidth: '300px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                        title={isObject ? getFieldLabel(field) : formattedValue}
                      >
                        {isObject ? (
                          <button
                            onClick={() => setJsonModal({
                              open: true,
                              title: `${getFieldLabel(field)} - ${assistant.name || assistant.assistant_id}`,
                              data: fieldValue
                            })}
                            style={{
                              padding: '4px 8px',
                              backgroundColor: '#007bff',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            Show JSON
                          </button>
                        ) : (
                          formattedValue
                        )}
                      </td>
                    );
                  })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* JSON Modal */}
      {jsonModal.open && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001
        }}>
          <div style={{
            background: 'white',
            padding: '20px',
            borderRadius: '8px',
            maxWidth: '90vw',
            maxHeight: '90vh',
            width: '800px',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <h3 style={{ marginTop: 0 }}>{jsonModal.title}</h3>
            <div style={{
              flex: 1,
              overflow: 'auto',
              background: '#f5f5f5',
              padding: '15px',
              borderRadius: '4px',
              fontFamily: 'monospace',
              fontSize: '14px',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word'
            }}>
              {JSON.stringify(jsonModal.data, null, 2)}
            </div>
            <button
              onClick={() => setJsonModal({ open: false, title: '', data: null })}
              style={{
                marginTop: '15px',
                padding: '8px 16px',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                alignSelf: 'flex-end'
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DealerList;
